import { CostData, CostFilters, CostError, ApiResponse } from '../types';

class CostService {
  private baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

  private async apiRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = localStorage.getItem('auth_token');

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    try {
      console.log('CostService: Making API request to', `${this.baseUrl}${endpoint}`);
      const response = await fetch(`${this.baseUrl}${endpoint}`, config);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('CostService: API request failed', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });

        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      console.log('CostService: API request successful', result);

      return result;
    } catch (error) {
      console.error('CostService: API request error', error);
      throw error;
    }
  }

  async getCostSummary(filters?: CostFilters): Promise<CostData> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.cloud?.length) {
          params.append('clouds', filters.cloud.join(','));
        }
        if (filters.accounts?.length) {
          params.append('accounts', filters.accounts.join(','));
        }
        if (filters.regions?.length) {
          params.append('regions', filters.regions.join(','));
        }
        if (filters.resourceTypes?.length) {
          params.append('resourceTypes', filters.resourceTypes.join(','));
        }
        if (filters.dateRange) {
          params.append('startDate', filters.dateRange.start);
          params.append('endDate', filters.dateRange.end);
        }
        if (filters.costRange) {
          params.append('minCost', filters.costRange.min.toString());
          params.append('maxCost', filters.costRange.max.toString());
        }
      }

      const queryString = params.toString();
      const endpoint = `/api/cost/summary${queryString ? `?${queryString}` : ''}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to fetch cost summary');
      }

      // Transform the API response into the expected CostData format
      const apiData = response.data;
      const transformedData: CostData = {
        summary: {
          totalSpend: apiData.totalSpend || 0,
          monthlyDelta: apiData.monthlyDelta || 0,
          byType: apiData.byType || {
            compute: 0,
            storage: 0,
            networking: 0,
            github: 0,
            other: 0
          },
          byProvider: apiData.byProvider || {
            aws: 0,
            gcp: 0,
            github: 0
          },
          serviceCount: apiData.serviceCount || 0
        },
        clouds: {
          aws: {
            accounts: apiData.accounts?.aws || [],
            regions: [], // TODO: Add region breakdown when available
            services: apiData.accounts?.aws?.[0]?.services || [],
            totalSpend: apiData.byProvider?.aws || 0
          },
          gcp: {
            projects: apiData.accounts?.gcp || [],
            regions: [], // TODO: Add region breakdown when available  
            services: apiData.accounts?.gcp?.[0]?.services || [],
            totalSpend: apiData.byProvider?.gcp || 0
          },
          github: {
            orgs: apiData.accounts?.github || [],
            usage: [], // TODO: Add usage breakdown when available
            totalSpend: apiData.byProvider?.github || 0
          }
        },
        lastUpdated: new Date().toISOString()
      };

      return transformedData;
    } catch (error: any) {
      console.error('CostService: Error fetching cost summary:', error);
      throw this.createCostError(error.message, 'multiple');
    }
  }

  async getAWSCostData(filters?: CostFilters): Promise<any> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.accounts?.length) {
          params.append('accounts', filters.accounts.join(','));
        }
        if (filters.regions?.length) {
          params.append('regions', filters.regions.join(','));
        }
        if (filters.dateRange) {
          params.append('startDate', filters.dateRange.start);
          params.append('endDate', filters.dateRange.end);
        }
      }

      const queryString = params.toString();
      const endpoint = `/api/cost/aws${queryString ? `?${queryString}` : ''}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch AWS cost data');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching AWS cost data:', error);
      throw this.createCostError(error.message, 'aws');
    }
  }

  async getGCPCostData(filters?: CostFilters): Promise<any> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.accounts?.length) {
          params.append('projects', filters.accounts.join(','));
        }
        if (filters.regions?.length) {
          params.append('regions', filters.regions.join(','));
        }
        if (filters.dateRange) {
          params.append('startDate', filters.dateRange.start);
          params.append('endDate', filters.dateRange.end);
        }
      }

      const queryString = params.toString();
      const endpoint = `/api/cost/gcp${queryString ? `?${queryString}` : ''}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch GCP cost data');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching GCP cost data:', error);
      throw this.createCostError(error.message, 'gcp');
    }
  }

  async getGitHubCostData(filters?: CostFilters): Promise<any> {
    try {
      const params = new URLSearchParams();

      if (filters) {
        if (filters.accounts?.length) {
          params.append('orgs', filters.accounts.join(','));
        }
        if (filters.dateRange) {
          params.append('startDate', filters.dateRange.start);
          params.append('endDate', filters.dateRange.end);
        }
      }

      const queryString = params.toString();
      const endpoint = `/api/cost/github${queryString ? `?${queryString}` : ''}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch GitHub cost data');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching GitHub cost data:', error);
      throw this.createCostError(error.message, 'github');
    }
  }

  async getCostTrends(
    cloud: 'aws' | 'gcp' | 'github',
    period: 'daily' | 'weekly' | 'monthly' = 'daily',
    days: number = 30
  ): Promise<any> {
    try {
      const params = new URLSearchParams({
        cloud,
        period,
        days: days.toString()
      });

      const endpoint = `/api/cost/trends?${params.toString()}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch cost trends');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching cost trends:', error);
      throw this.createCostError(error.message, cloud);
    }
  }

  async getThreeMonthAnalysis(filters?: CostFilters): Promise<any> {
    try {
      const params = new URLSearchParams();

      if (filters?.accounts?.length) {
        params.append('accountId', filters.accounts[0]);
      }

      const queryString = params.toString();
      const endpoint = `/api/cost/analysis/three-months${queryString ? `?${queryString}` : ''}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch 3-month analysis');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching 3-month analysis:', error);
      throw this.createCostError(error.message, 'multiple');
    }
  }

  async getTagBasedCosts(tagKey?: string): Promise<any> {
    try {
      const queryString = tagKey ? `?tagKey=${encodeURIComponent(tagKey)}` : '';
      const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/analysis/tags${queryString}`);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch tag-based costs');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching tag-based costs:', error);
      throw this.createCostError(error.message, 'multiple');
    }
  }

  async getTwelveMonthAnalysis(): Promise<any> {
    try {
      const response = await this.apiRequest<ApiResponse<any>>('/api/cost/analysis/twelve-months');

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch 12-month analysis');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching 12-month analysis:', error);
      throw this.createCostError(error.message, 'multiple');
    }
  }

  async getCurrentMonthAnalysis(filters?: CostFilters): Promise<any> {
    try {
      const params = new URLSearchParams();

      if (filters?.accounts?.length) {
        params.append('accountId', filters.accounts[0]);
      }

      const queryString = params.toString();
      const endpoint = `/api/cost/analysis/current-month${queryString ? `?${queryString}` : ''}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch current month analysis');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching current month analysis:', error);
      throw this.createCostError(error.message, 'multiple');
    }
  }

  async getCostForecast(filters?: CostFilters): Promise<any> {
    try {
      const params = new URLSearchParams();

      if (filters?.accounts?.length) {
        params.append('accountId', filters.accounts[0]);
      }

      const queryString = params.toString();
      const endpoint = `/api/cost/forecast${queryString ? `?${queryString}` : ''}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch cost forecast');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching cost forecast:', error);
      throw this.createCostError(error.message, 'multiple');
    }
  }

  async getRegionalCostBreakdown(
    cloud: 'aws' | 'gcp',
    filters?: CostFilters
  ): Promise<any> {
    try {
      const params = new URLSearchParams({ cloud });

      if (filters) {
        if (filters.accounts?.length) {
          const accountParam = cloud === 'aws' ? 'accounts' : 'projects';
          params.append(accountParam, filters.accounts.join(','));
        }
        if (filters.dateRange) {
          params.append('startDate', filters.dateRange.start);
          params.append('endDate', filters.dateRange.end);
        }
      }

      const endpoint = `/api/cost/regional?${params.toString()}`;

      const response = await this.apiRequest<ApiResponse<any>>(endpoint);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch regional cost breakdown');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching regional cost breakdown:', error);
      throw this.createCostError(error.message, cloud);
    }
  }

  async getPricingAndUtilization(): Promise<any> {
    try {
      const response = await this.apiRequest<ApiResponse<any>>('/api/cost/analysis/pricing-utilization');

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch pricing and utilization data');
      }

      return response.data;
    } catch (error: any) {
      console.error('CostService: Error fetching pricing and utilization:', error);
      // Return default zeros instead of throwing - so UI always renders
      return {
        aws: {
          pricingBreakdown: {
            onDemand: { cost: 0, percentage: 0 },
            reserved: { cost: 0, percentage: 0, savings: 0 },
            savingsPlans: { cost: 0, percentage: 0, savings: 0 },
            spot: { cost: 0, percentage: 0, savings: 0 },
            total: 0
          },
          reservedInstances: { utilization: 0, coverage: 0 },
          savingsPlans: { utilization: 0, coverage: 0 }
        },
        gcp: {
          commitments: {
            onDemand: { cost: 0, percentage: 0 },
            cud: { cost: 0, percentage: 0, savings: 0 },
            sud: { cost: 0, percentage: 0, savings: 0 },
            total: 0
          }
        },
        monthlyPayments: [],
        connectedAccounts: { aws: 0, gcp: 0, github: 0 }
      };
    }
  }

  // ==========================================================================
  // BILLS — Monthly bill uploads + manual entries
  // ==========================================================================

  async getBills(opts: { month?: string; provider?: string; status?: string } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (opts.month) params.append('month', opts.month);
    if (opts.provider) params.append('provider', opts.provider);
    if (opts.status) params.append('status', opts.status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.apiRequest<ApiResponse<any[]>>(`/api/cost/bills${qs}`);
    if (!response.success) throw new Error(response.error || 'Failed to fetch bills');
    return response.data || [];
  }

  async createBill(payload: {
    month: string;
    provider: 'aws' | 'gcp' | 'github' | 'custom';
    accountName: string;
    amount: number;
    currency?: string;
    status?: 'paid' | 'pending' | 'overdue';
    dueDate?: string;
    paidDate?: string;
    invoiceNumber?: string;
    file?: File | null;
  }): Promise<any> {
    const token = localStorage.getItem('auth_token');
    // FormData (multipart) when a PDF is attached, otherwise plain JSON.
    let init: RequestInit;
    if (payload.file) {
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (k === 'file' || v === undefined || v === null) return;
        fd.append(k, String(v));
      });
      fd.append('file', payload.file);
      init = {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: fd
      };
    } else {
      const { file: _ignored, ...json } = payload;
      init = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(json)
      };
    }
    const response = await fetch(`${this.baseUrl}/api/cost/bills`, init);
    const result = await response.json();
    if (!response.ok || !result.success) {
      throw new Error(result.error || `HTTP ${response.status}`);
    }
    return result.data;
  }

  async updateBill(id: string, payload: Partial<{
    month: string;
    provider: string;
    accountName: string;
    amount: number;
    currency: string;
    status: string;
    dueDate: string;
    paidDate: string;
    invoiceNumber: string;
  }>): Promise<any> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/bills/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    if (!response.success) throw new Error(response.error || 'Failed to update bill');
    return response.data;
  }

  async deleteBill(id: string): Promise<any> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/bills/${id}`, {
      method: 'DELETE'
    });
    if (!response.success) throw new Error(response.error || 'Failed to delete bill');
    return response.data;
  }

  async getBillsSummary(month?: string): Promise<any> {
    const qs = month ? `?month=${encodeURIComponent(month)}` : '';
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/bills/summary${qs}`);
    if (!response.success) throw new Error(response.error || 'Failed to fetch bills summary');
    return response.data;
  }

  async parseBillsFromDrive(folderUrl: string, accessToken?: string): Promise<any[]> {
    const token = localStorage.getItem('auth_token');
    const response = await fetch(`${this.baseUrl}/api/cost/bills/drive-parse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ folderUrl, ...(accessToken && { accessToken }) }),
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.error || `HTTP ${response.status}`);
    return result.data || [];
  }

  async bulkParseBills(files: File[]): Promise<any[]> {
    const token = localStorage.getItem('auth_token');
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    const response = await fetch(`${this.baseUrl}/api/cost/bills/bulk-parse`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd
    });
    const result = await response.json();
    if (!response.ok || !result.success) throw new Error(result.error || `HTTP ${response.status}`);
    return result.data || [];
  }

  // Returns a short-lived signed S3 URL for the bill's PDF.
  // `inline=true` → preview in iframe; `false` → download.
  async getBillFileUrl(id: string, inline = false): Promise<string> {
    const path = inline ? 'view' : 'download';
    const response = await this.apiRequest<ApiResponse<any> & { url?: string }>(
      `/api/cost/bills/${id}/${path}`
    );
    if (!response.success || !response.url) {
      throw new Error(response.error || 'Failed to get bill file URL');
    }
    return response.url;
  }

  // ==========================================================================
  // RECONCILIATION — drives the Reconciliation tab
  // For each month in the year: AWS + GCP + Custom vs Bills uploaded → diff
  // ==========================================================================
  async getReconciliation(year?: number): Promise<{ data: any[]; summary: any; year: number }> {
    const qs = year ? `?year=${year}` : '';
    const response = await this.apiRequest<ApiResponse<any[]> & { summary?: any; year?: number }>(
      `/api/cost/reconciliation${qs}`
    );
    if (!response.success) throw new Error(response.error || 'Failed to fetch reconciliation');
    return {
      data: response.data || [],
      summary: response.summary || {},
      year: response.year || year || new Date().getFullYear()
    };
  }

  // ==========================================================================
  // CUSTOM REPORT — drives the Report tab (AWS Cost Explorer-style)
  // ==========================================================================
  async getCustomReport(opts: {
    startDate: string;
    endDate: string;
    granularity?: 'DAILY' | 'MONTHLY';
    groupBy?: 'service' | 'account' | 'region';
    costType?: 'UnblendedCost' | 'BlendedCost' | 'AmortizedCost' | 'NetUnblendedCost' | 'NetAmortizedCost';
    services?: string[];
    accounts?: string[];
    regions?: string[];
  }): Promise<{
    labels: string[];
    groups: Array<{ name: string; data: number[]; total: number }>;
    summary: { totalCost: number; averageCost: number; highestCost: number; lowestCost: number; trend: number };
    dimension: string;
    granularity: string;
    costType?: string;
    message?: string;
  }> {
    const params = new URLSearchParams();
    params.append('startDate', opts.startDate);
    params.append('endDate', opts.endDate);
    if (opts.granularity) params.append('granularity', opts.granularity);
    if (opts.groupBy) params.append('groupBy', opts.groupBy);
    if (opts.costType) params.append('costType', opts.costType);
    if (opts.services?.length) params.append('services', opts.services.join(','));
    if (opts.accounts?.length) params.append('accounts', opts.accounts.join(','));
    if (opts.regions?.length) params.append('regions', opts.regions.join(','));

    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/analysis/report?${params.toString()}`);
    if (!response.success) throw new Error(response.error || 'Failed to fetch report');
    return response.data;
  }

  // ==========================================================================
  // CUSTOM COST DATA — Vendors + Entries
  // ==========================================================================

  async getCustomVendors(opts: { category?: string; active?: boolean } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (opts.category) params.append('category', opts.category);
    if (opts.active !== undefined) params.append('active', String(opts.active));
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.apiRequest<ApiResponse<any[]>>(`/api/cost/custom-data/vendors${qs}`);
    if (!response.success) throw new Error(response.error || 'Failed to fetch vendors');
    return response.data || [];
  }

  async createCustomVendor(payload: { name: string; category?: string; description?: string; website?: string; defaultCurrency?: string }): Promise<any> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/custom-data/vendors`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.success) throw new Error(response.error || 'Failed to create vendor');
    return response.data;
  }

  async updateCustomVendor(id: string, payload: Partial<{ name: string; category: string; description: string; website: string; defaultCurrency: string; active: boolean }>): Promise<any> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/custom-data/vendors/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    if (!response.success) throw new Error(response.error || 'Failed to update vendor');
    return response.data;
  }

  async deleteCustomVendor(id: string): Promise<any> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/custom-data/vendors/${id}`, {
      method: 'DELETE'
    });
    if (!response.success) throw new Error(response.error || 'Failed to delete vendor');
    return response.data;
  }

  async getCustomEntries(opts: { month?: string; vendorId?: string; source?: 'manual' | 'csv-import' } = {}): Promise<any[]> {
    const params = new URLSearchParams();
    if (opts.month) params.append('month', opts.month);
    if (opts.vendorId) params.append('vendorId', opts.vendorId);
    if (opts.source) params.append('source', opts.source);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await this.apiRequest<ApiResponse<any[]>>(`/api/cost/custom-data/entries${qs}`);
    if (!response.success) throw new Error(response.error || 'Failed to fetch entries');
    return response.data || [];
  }

  async createCustomEntry(payload: {
    vendorName?: string;
    vendorId?: string;
    category?: string;
    amount: number;
    currency?: string;
    month: string;
    dateRange?: { start: string; end: string };
    notes?: string;
    source?: 'manual' | 'csv-import';
  }): Promise<any> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/custom-data/entries`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    if (!response.success) throw new Error(response.error || 'Failed to create entry');
    return response.data;
  }

  async updateCustomEntry(id: string, payload: Partial<{ vendorName: string; vendorId: string; category: string; amount: number; currency: string; month: string; dateRange: { start: string; end: string }; notes: string }>): Promise<any> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/custom-data/entries/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    });
    if (!response.success) throw new Error(response.error || 'Failed to update entry');
    return response.data;
  }

  async deleteCustomEntry(id: string): Promise<any> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/custom-data/entries/${id}`, {
      method: 'DELETE'
    });
    if (!response.success) throw new Error(response.error || 'Failed to delete entry');
    return response.data;
  }

  async bulkImportCustomEntries(entries: Array<{
    vendorName: string;
    category?: string;
    amount: number;
    currency?: string;
    month: string;
    dateRange?: { start: string; end: string };
    notes?: string;
  }>): Promise<{ insertedCount: number; vendorsTouched: number; errors: Array<{ row: number; error: string }>; entries: any[] }> {
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/custom-data/entries/bulk`, {
      method: 'POST',
      body: JSON.stringify({ entries })
    });
    if (!response.success) throw new Error(response.error || 'Bulk import failed');
    return response.data;
  }

  async getCustomDataSummary(month?: string): Promise<{ month: string; total: number; byCategory: Array<{ category: string; total: number; count: number }> }> {
    const qs = month ? `?month=${encodeURIComponent(month)}` : '';
    const response = await this.apiRequest<ApiResponse<any>>(`/api/cost/custom-data/summary${qs}`);
    if (!response.success) throw new Error(response.error || 'Failed to fetch summary');
    return response.data;
  }

  private createCostError(message: string, service: 'aws' | 'gcp' | 'github' | 'multiple'): CostError {
    return {
      code: 'COST_FETCH_ERROR',
      message,
      service,
      timestamp: new Date().toISOString()
    };
  }

  // Mock data generators for development/testing
  generateMockCostData(): CostData {
    const now = new Date().toISOString();

    // Calculate totals that match the cloud provider costs
    const awsTotal = 325000;
    const gcpTotal = 95000;
    const githubTotal = 12291.4;
    const totalSpend = awsTotal + gcpTotal + githubTotal; // 432291.4

    // Breakdown by type - should sum to totalSpend
    const computeCost = 160000; // EC2 + Compute Engine
    const storageCost = 80000; // S3 + Cloud Storage + Redshift
    const networkingCost = 40000; // Networking services
    const githubCost = githubTotal; // 12291.4
    const otherCost = totalSpend - computeCost - storageCost - networkingCost - githubCost; // ~140000

    return {
      summary: {
        totalSpend: totalSpend,
        monthlyDelta: 5200, // Monthly delta amount in dollars (12% of 43229 = ~5200)
        byType: {
          compute: computeCost,
          storage: storageCost,
          networking: networkingCost,
          github: githubCost,
          other: otherCost
        },
        byProvider: {
          aws: awsTotal,
          gcp: gcpTotal,
          github: githubTotal
        }
      },
      clouds: {
        aws: {
          accounts: [
            {
              accountId: '123456789012',
              accountName: 'Production (US-East)',
              totalCost: 185000,
              services: [
                { serviceName: 'EC2', totalCost: 95000, percentage: 51.4, trend: { direction: 'up', percentage: 5.2, period: 'monthly' } },
                { serviceName: 'RDS', totalCost: 45000, percentage: 24.3, trend: { direction: 'stable', percentage: 0.5, period: 'monthly' } },
                { serviceName: 'S3', totalCost: 25000, percentage: 13.5, trend: { direction: 'down', percentage: 2.1, period: 'monthly' } },
                { serviceName: 'Lambda', totalCost: 20000, percentage: 10.8, trend: { direction: 'up', percentage: 8.3, period: 'monthly' } }
              ],
              trend: { direction: 'up', percentage: 5.8, period: 'monthly' }
            },
            {
              accountId: '123456789013',
              accountName: 'Staging',
              totalCost: 45000,
              services: [
                { serviceName: 'EC2', totalCost: 25000, percentage: 55.6, trend: { direction: 'stable', percentage: 1.2, period: 'monthly' } },
                { serviceName: 'RDS', totalCost: 15000, percentage: 33.3, trend: { direction: 'up', percentage: 3.5, period: 'monthly' } },
                { serviceName: 'S3', totalCost: 5000, percentage: 11.1, trend: { direction: 'down', percentage: 1.8, period: 'monthly' } }
              ],
              trend: { direction: 'stable', percentage: 2.1, period: 'monthly' }
            },
            {
              accountId: '123456789014',
              accountName: 'Dev-Berlin',
              totalCost: 28000,
              services: [
                { serviceName: 'EC2', totalCost: 15000, percentage: 53.6, trend: { direction: 'up', percentage: 3.2, period: 'monthly' } },
                { serviceName: 'RDS', totalCost: 8000, percentage: 28.6, trend: { direction: 'stable', percentage: 0.8, period: 'monthly' } },
                { serviceName: 'S3', totalCost: 3000, percentage: 10.7, trend: { direction: 'down', percentage: 1.5, period: 'monthly' } },
                { serviceName: 'CloudWatch', totalCost: 2000, percentage: 7.1, trend: { direction: 'up', percentage: 12.3, period: 'monthly' } }
              ],
              trend: { direction: 'up', percentage: 4.1, period: 'monthly' }
            },
            {
              accountId: '123456789015',
              accountName: 'Analytics',
              totalCost: 67000,
              services: [
                { serviceName: 'Redshift', totalCost: 35000, percentage: 52.2, trend: { direction: 'up', percentage: 8.5, period: 'monthly' } },
                { serviceName: 'S3', totalCost: 18000, percentage: 26.9, trend: { direction: 'stable', percentage: 1.2, period: 'monthly' } },
                { serviceName: 'Glue', totalCost: 8000, percentage: 11.9, trend: { direction: 'up', percentage: 15.6, period: 'monthly' } },
                { serviceName: 'Athena', totalCost: 6000, percentage: 9.0, trend: { direction: 'stable', percentage: 2.3, period: 'monthly' } }
              ],
              trend: { direction: 'up', percentage: 7.8, period: 'monthly' }
            }
          ],
          regions: [
            { region: 'us-east-1', totalCost: 150000, services: [], percentage: 65.2 },
            { region: 'us-west-2', totalCost: 50000, services: [], percentage: 21.7 },
            { region: 'eu-west-1', totalCost: 30000, services: [], percentage: 13.1 }
          ],
          services: [
            { serviceName: 'EC2', totalCost: 120000, percentage: 52.2, trend: { direction: 'up', percentage: 4.8, period: 'monthly' } },
            { serviceName: 'RDS', totalCost: 60000, percentage: 26.1, trend: { direction: 'stable', percentage: 1.2, period: 'monthly' } },
            { serviceName: 'S3', totalCost: 30000, percentage: 13.0, trend: { direction: 'down', percentage: 2.0, period: 'monthly' } },
            { serviceName: 'Lambda', totalCost: 20000, percentage: 8.7, trend: { direction: 'up', percentage: 7.5, period: 'monthly' } }
          ],
          totalSpend: 325000
        },
        gcp: {
          projects: [
            {
              projectId: 'my-project-prod',
              projectName: 'Production Project',
              totalCost: 35000,
              services: [
                { serviceName: 'Compute Engine', totalCost: 20000, percentage: 57.1, trend: { direction: 'stable', percentage: 1.5, period: 'monthly' } },
                { serviceName: 'BigQuery', totalCost: 10000, percentage: 28.6, trend: { direction: 'up', percentage: 6.2, period: 'monthly' } },
                { serviceName: 'Cloud Storage', totalCost: 5000, percentage: 14.3, trend: { direction: 'down', percentage: 3.1, period: 'monthly' } }
              ],
              trend: { direction: 'up', percentage: 3.2, period: 'monthly' }
            },
            {
              projectId: 'dev-analytics-421',
              projectName: 'Dev Analytics',
              totalCost: 18000,
              services: [
                { serviceName: 'BigQuery', totalCost: 12000, percentage: 66.7, trend: { direction: 'up', percentage: 8.1, period: 'monthly' } },
                { serviceName: 'Compute Engine', totalCost: 4000, percentage: 22.2, trend: { direction: 'stable', percentage: 0.5, period: 'monthly' } },
                { serviceName: 'Cloud Storage', totalCost: 2000, percentage: 11.1, trend: { direction: 'down', percentage: 2.3, period: 'monthly' } }
              ],
              trend: { direction: 'up', percentage: 5.7, period: 'monthly' }
            },
            {
              projectId: 'ml-experiments-789',
              projectName: 'ML Experiments',
              totalCost: 42000,
              services: [
                { serviceName: 'Vertex AI', totalCost: 28000, percentage: 66.7, trend: { direction: 'up', percentage: 25.3, period: 'monthly' } },
                { serviceName: 'Compute Engine', totalCost: 8000, percentage: 19.0, trend: { direction: 'up', percentage: 12.1, period: 'monthly' } },
                { serviceName: 'Cloud Storage', totalCost: 6000, percentage: 14.3, trend: { direction: 'stable', percentage: 1.8, period: 'monthly' } }
              ],
              trend: { direction: 'up', percentage: 18.9, period: 'monthly' }
            }
          ],
          regions: [
            { region: 'us-central1', totalCost: 25000, services: [], percentage: 71.4 },
            { region: 'europe-west1', totalCost: 10000, services: [], percentage: 28.6 }
          ],
          services: [
            { serviceName: 'Compute Engine', totalCost: 20000, percentage: 57.1, trend: { direction: 'stable', percentage: 1.5, period: 'monthly' } },
            { serviceName: 'BigQuery', totalCost: 10000, percentage: 28.6, trend: { direction: 'up', percentage: 6.2, period: 'monthly' } },
            { serviceName: 'Cloud Storage', totalCost: 5000, percentage: 14.3, trend: { direction: 'down', percentage: 3.1, period: 'monthly' } }
          ],
          totalSpend: 95000
        },
        github: {
          orgs: [
            {
              orgName: 'company-main',
              totalCost: 9491.4,
              usage: [
                { type: 'actions', cost: 4500, usage: 180000, unit: 'minutes' },
                { type: 'packages', cost: 2491.4, usage: 500, unit: 'GB' },
                { type: 'storage', cost: 2000, usage: 100, unit: 'GB' },
                { type: 'bandwidth', cost: 500, usage: 1000, unit: 'GB' }
              ],
              trend: { direction: 'up', percentage: 15.2, period: 'monthly' }
            },
            {
              orgName: 'open-source-team',
              totalCost: 2800,
              usage: [
                { type: 'actions', cost: 1800, usage: 72000, unit: 'minutes' },
                { type: 'packages', cost: 600, usage: 120, unit: 'GB' },
                { type: 'storage', cost: 300, usage: 50, unit: 'GB' },
                { type: 'bandwidth', cost: 100, usage: 200, unit: 'GB' }
              ],
              trend: { direction: 'stable', percentage: 2.3, period: 'monthly' }
            }
          ],
          usage: [
            { type: 'actions', cost: 4500, usage: 180000, unit: 'minutes' },
            { type: 'packages', cost: 2491.4, usage: 500, unit: 'GB' },
            { type: 'storage', cost: 2000, usage: 100, unit: 'GB' },
            { type: 'bandwidth', cost: 500, usage: 1000, unit: 'GB' }
          ],
          totalSpend: 12291.4
        }
      },
      lastUpdated: now
    };
  }
}

export const costService = new CostService();